<template>
  <div class="home">
    <b-breadcrumb :items="breadcrumbs"/>
    <h1 v-text="getServerName()"></h1>
    <transient-status :isLoading="isLoading" :error="error">
      <div v-if="serverDetails">
        <b-alert v-if="serverMapping && serverMapping.is_inactive" variant="warning" show>This server is marked as inactive.</b-alert>
        <b-alert v-if="!serverMapping" variant="warning" show>This server is not registered with Landscape.</b-alert>
        <div v-if="lastPing">Information below was accurate as of {{ lastPing.raised_at_utc.value | moment('from', 'now') }}:</div>
        <ul>
          <li>
            Most recent report
            <b-badge variant="light"
              >{{ serverDetails.min_max[0].latest_date.value | moment('DD/MM/YYYY h:mm a') }}
            </b-badge>
          </li>
          <li>
            First report received
            <b-badge variant="light"
              >{{ serverDetails.min_max[0].earliest_date.value
                | moment('DD/MM/YYYY h:mm a') }}</b-badge>
          </li>
          <li>
            # of reports
            <b-badge variant="light">{{ serverDetails.report_count[0].report_count | commas }}</b-badge>
          </li>
          <li>
            Issues in past {{ serverDetails.issue_count[0].num_of_days }} days
            <b-badge variant="light">{{ serverDetails.issue_count[0].report_count | commas }}</b-badge>
          </li>
          <li v-if="lastPing">
            Version
            <b-badge variant="light">{{ lastPing.component_version }}</b-badge>
          </li>
          <li v-if="lastPing">
            GMT Offset
            <b-badge variant="light">
              {{ lastPing.gmt_offset >= 0 ? '+' : '' }}
              {{ lastPing.gmt_offset }} minutes
            </b-badge>
          </li>
          <li v-if="lastPing">
            Processor Architecture
            <b-badge variant="light">{{ lastPing.is_64bit ? '64' : '32' }}-bit</b-badge>
          </li>
        </ul>

        <b-button-group v-if="serverMapping">
          <b-button v-if="!serverMapping.is_inactive" @click="markAsInactive" size="lg" variant="danger">Mark server as <b>inactive</b></b-button>
          <b-button v-if="serverMapping.is_inactive" @click="markAsActive" size="lg" variant="danger">Mark server as <b>active</b></b-button>
          <b-button @click="markAllAsResolved" size="lg" variant="success">
            Mark all as resolved
          </b-button>
        </b-button-group>

        <h2>Most Recent Reports</h2>
        <reports :hide-site-name="true" :reports="serverDetails.recent"/>
      </div>
    </transient-status>
    <countdown v-if="!isLoading" interval="60" @tickerReachedZero="fetchData" />
  </div>
</template>

<script>
import TransientStatus from '../components/TransientStatus.vue';
import Reports from '../components/Reports.vue';
import Countdown from '../components/Countdown.vue';

export default {
  components: { TransientStatus, Reports, Countdown },

  data() {
    return {
      breadcrumbs: [
        { text: 'Servers', to: { name: 'servers' } },
        { text: this.$route.params.server, active: true },
      ],
      isLoading: true,
      error: null,
      server: this.$route.params.server,
      serverDetails: null,
      serverMapping: null,
    };
  },

  mounted() {
    this.fetchData();
  },

  computed: {
    lastPing() {
      if (!this.serverDetails || !this.serverDetails.last_ping) {
        return null;
      }
      return this.serverDetails.last_ping[0];
    }
  },

  methods: {
    getServerName() {
      if (this.serverMapping) {
        return `${this.serverMapping.region}, ${this.serverMapping.store}`;
      }
      return this.server;
    },
    fetchData() {
      this.isLoading = true;
      const { access_token } = this.$store.state.oidcStore;
      this.$reportingApi.renderPage('landscape', access_token, {
        pageName: 'server_details',
        parameters: {
          location: this.server,
        },
      })
      .then((serverDetails) => {
        this.serverDetails = serverDetails;
        // if the server is not registered with landscape, this will not return anything
        if (this.serverDetails.store_details && this.serverDetails.store_details.length > 0) {
          this.serverMapping = this.serverDetails.store_details[0];
        }
      })
      .catch((e) => {
        this.error = e.message;
      })
      .finally(() => {
        this.isLoading = false;
      }); 
    },

    markAllAsResolved() {
      this.isLoading = true;
      const { access_token } = this.$store.state.oidcStore;
      this.$reportingApi.runAction('landscape', access_token, {
        actionName: 'markLocationAsResolved',
        parameters: {
          location: this.server,
        },
      })
      .then((d) => setTimeout(() => this.fetchData(), 800))
      .catch((e) => {
        console.error('Error resolving reports', e.message);
        this.error = `Error resolving reports: ${e.message}`;
        this.isLoading = false;
      });
    },

    markAsInactive() {
      this.toggleInactiveFlag(true);
    },

    markAsActive() {
      this.toggleInactiveFlag(false);
    },

    toggleInactiveFlag(inactive_flag) {
      this.isLoading = true;
      const { access_token } = this.$store.state.oidcStore;
      this.$reportingApi.runAction('landscape', access_token, {
        actionName: 'toggleStoreActiveFlag',
        parameters: {
          inactive_flag,
          location: this.server,
        },
      })
      .then((d) => {
        const { serverMapping } = this;
        serverMapping.is_inactive = inactive_flag;
        this.serverMapping = serverMapping;
      })
      .catch((e) => {
        console.error('Error resolving reports', e.message);
        this.error = `Error resolving reports: ${e.message}`;
      })
      .finally(() => {
        this.isLoading = false;
      });
    },
  },
};
</script>
